import type { UPGRADE_PROMPT_SURFACES } from "@reactive-resume/dto";
import { useEffect, useRef } from "react";

import { trackFunnelEvent } from "@/client/services/analytics/track-funnel-event";

export type UpgradePromptSurface = (typeof UPGRADE_PROMPT_SURFACES)[number];

/**
 * Reports `upgrade_prompt_viewed` once per mount of a price or upgrade surface.
 *
 * Pass `undefined` (or `active: false`) while the surface is not actually on screen. The
 * server keeps one row per user, per surface, per UTC day (`DEDUP_KEY_PROPS` in
 * funnel-events.service.ts), so a surface that mounts again the same day adds no row.
 * A host that switches to a different surface while mounted reports the new one too.
 */
export const useUpgradePromptViewed = (
  surface: UpgradePromptSurface | undefined,
  active = true,
): void => {
  const reportedSurface = useRef<UpgradePromptSurface | undefined>();
  useEffect(() => {
    if (!surface || !active || reportedSurface.current === surface) return;
    reportedSurface.current = surface;
    trackFunnelEvent("upgrade_prompt_viewed", { surface });
  }, [surface, active]);
};
